/** View data hooks — My Day, Inbox, workload, leave overlay, saved views. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LeaveType, SavedViewScope, ViewType } from '@virtuetasks/types';
import { api, qs } from '@/lib/api';
import type { TaskView } from '@/features/tasks/api';

export interface MyDayBuckets {
  overdue: TaskView[];
  today: TaskView[];
  nextUp: TaskView[];
}

export function useMyDay() {
  return useQuery({
    queryKey: ['my-day'],
    queryFn: async () => (await api.get<MyDayBuckets>('/tasks/my-day')).data,
  });
}

/** Personal focus order — never touches the shared board rank (§5-F11). */
export function useSetMyDayOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskOrder: string[]) =>
      (await api.put<{ taskOrder: string[] }>('/tasks/my-day/order', { taskOrder })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['my-day'] }),
  });
}

export function useInbox() {
  return useQuery({
    queryKey: ['inbox'],
    queryFn: async () => (await api.get<TaskView[]>('/tasks/inbox')).data,
  });
}

export interface WorkloadRow {
  userId: string;
  name: string;
  capacityMinutes: number;
  assignedMinutes: number;
  utilisation: number;
  workingDays: number;
  leaveDays: number;
  taskCount: number;
}

export function useWorkload(from: Date, to: Date, filters: { teamId?: string; projectId?: string } = {}) {
  return useQuery({
    queryKey: ['workload', from.toISOString(), to.toISOString(), filters],
    queryFn: async () =>
      (await api.get<WorkloadRow[]>(`/users/workload${qs({ from, to, ...filters })}`)).data,
  });
}

export interface LeaveCalendarEntry {
  _id: string;
  userId: string;
  userName: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
}

export function useLeaveOverlay(from: Date, to: Date) {
  return useQuery({
    queryKey: ['leave-calendar', from.toISOString(), to.toISOString()],
    queryFn: async () =>
      (await api.get<LeaveCalendarEntry[]>(`/leaves/calendar${qs({ from, to })}`)).data,
  });
}

export interface SavedViewRecord {
  _id: string;
  name: string;
  icon?: string;
  scope: SavedViewScope;
  projectId?: string;
  viewType: ViewType;
  filters: Record<string, unknown>;
  groupBy?: string;
  sortBy?: string;
  sortDir: 'asc' | 'desc';
  visibleColumns: string[];
  isPinned: boolean;
  order: number;
}

export function useSavedViews() {
  return useQuery({
    queryKey: ['views'],
    queryFn: async () => (await api.get<SavedViewRecord[]>('/views')).data,
  });
}

export function useSaveView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) =>
      (await api.post<SavedViewRecord>('/views', input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['views'] }),
  });
}

export function useDeleteView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/views/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['views'] }),
  });
}

export interface SprintView {
  _id: string;
  projectId: string;
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  status: 'planned' | 'active' | 'completed';
  plannedTaskCount: number;
  plannedEstimateMinutes: number;
}

/**
 * Sprints for one project — V2 §7.7a `GET /projects/:id/sprints`.
 *
 * Used by the sprint planning screen and by the New-task modal's picker, so a
 * task can be committed to the active sprint at the moment it is written rather
 * than in a second pass.
 */
export function useProjectSprints(
  projectId: string | undefined,
  filters: { status?: 'planned' | 'active' | 'completed' } = {},
) {
  return useQuery({
    queryKey: ['project-sprints', projectId, filters],
    enabled: Boolean(projectId),
    queryFn: async () =>
      (await api.get<SprintView[]>(`/projects/${projectId}/sprints${qs(filters)}`)).data,
  });
}

export interface SprintCapacity {
  sprintId: string;
  workingDays: number;
  capacityMinutes: number;
  committedMinutes: number;
  members: { userId: string; name: string; capacityMinutes: number; committedMinutes: number; leaveDays: number }[];
}

export function useSprintCapacity(sprintId: string | undefined) {
  return useQuery({
    queryKey: ['sprint-capacity', sprintId],
    queryFn: async () => (await api.get<SprintCapacity>(`/sprints/${sprintId}/capacity`)).data,
    enabled: Boolean(sprintId),
  });
}

export interface AssigneeSuggestion {
  userId: string;
  name: string;
  score: number;
  reasons: string[];
  openTasks: number;
  weekLoadMinutes: number;
}

export function useAssigneeSuggestions(params: {
  projectId?: string;
  type?: string;
  estimateMinutes?: number;
  dueDate?: string;
}) {
  return useQuery({
    queryKey: ['suggest-assignees', params],
    queryFn: async () =>
      (await api.get<AssigneeSuggestion[]>(`/tasks/suggest-assignees${qs(params)}`)).data,
    enabled: Boolean(params.projectId),
  });
}
